import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

/* ---------------------------------------------------------------------------
   Text reveal — words rise into place one after another.
   Driven by CSS keyframes (see index.css `.word-reveal`) so the motion runs on
   the compositor and never hitches when React re-renders the rest of the page.
   Transform/opacity only; no clipping, no blur. Shared by the hero headline,
   the pricing page and the hero narrator.
--------------------------------------------------------------------------- */

export const TEXT_EASE = [0.22, 1, 0.36, 1];

/**
 * `true` once the web fonts have loaded (or after a short grace period).
 * Text reveals wait for this so words never re-flow mid-animation.
 */
export function useFontsReady(grace = 600) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let done = false;
    let raf = 0;
    const go = () => {
      if (done) return;
      done = true;
      // Two frames: let the browser commit the swapped font before the first transform.
      raf = requestAnimationFrame(() => {
        raf = requestAnimationFrame(() => setReady(true));
      });
    };
    const id = setTimeout(go, grace);
    if (typeof document !== "undefined" && document.fonts?.ready) document.fonts.ready.then(go).catch(go);
    else go();
    return () => {
      clearTimeout(id);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [grace]);
  return ready;
}

/**
 * Words rise one after another. `play=false` keeps them hidden (used to wait for
 * fonts or for the section to scroll into view); `reduced` renders them static.
 */
export function WordReveal({ text, as: Tag = "span", delay = 0, stagger = 0.06, duration = 0.9, distance = "0.6em", reduced = false, play = true, accentLast = 0, accentFirst = 0, accentClass = "text-lavender brand-tint", className, ...rest }) {
  const words = String(text).split(" ");
  const dist = typeof distance === "number" ? `${distance}px` : distance;
  return (
    <Tag className={cn("word-reveal", reduced ? "is-static" : play ? "is-playing" : "", className)} {...rest}>
      {words.map((w, k) => (
        <React.Fragment key={`${k}-${w}`}>
          <span className={cn("word-reveal__w", ((accentLast > 0 && k >= words.length - accentLast) || (accentFirst > 0 && k < accentFirst)) && accentClass)} style={{ "--wr-delay": `${(delay + k * stagger).toFixed(3)}s`, "--wr-dur": `${duration}s`, "--wr-dist": dist }}>
            {w}
          </span>
          {k < words.length - 1 ? " " : ""}
        </React.Fragment>
      ))}
    </Tag>
  );
}

/** A block that fades and rises. */
export const Rise = ({ delay = 0, duration = 0.9, y = 14, reduced = false, play = true, className, children, ...rest }) => (
  <motion.div initial={reduced ? false : { opacity: 0, y }} animate={play ? { opacity: 1, y: 0 } : undefined} transition={{ delay, duration, ease: TEXT_EASE }} className={className} {...rest}>
    {children}
  </motion.div>
);
